/** 실패 코드 → 셀러 말 매핑.
 *
 * **서버 에러 코드를 화면에 그대로 노출하지 않는다**(PRD 7.5 "에러"). 그리고 매핑이 앱과
 * 웹에 각각 있으면 같은 실패가 두 화면에서 다른 말로 보인다 — 그래서 공통에 둔다.
 *
 * ⭐ **파일 끝에 장수 상한을 말하는 문장 셋이 함께 있다**(`uploadRejectMessage` 의
 * `JOB_FULL` · `jobFullNoticeMessage` · `imageLimitSentence`). 셋 다 `MAX_IMAGES_PER_JOB`
 * 을 읽어 만든다. **마지막 하나는 실패 문구가 아니라 막히기 전의 안내인데도 여기 있다** —
 * 이유는 그 함수 주석에 있다.
 *
 * ⭐ **맨 끝에 `A-31f`(이제 다시 만들 수 없는 장) 문장도 있다**(`RETENTION_ENDED_COPY` ·
 * `retentionEndedCardBody`). 원본 보관이 끝난 장이 `SOURCE_GONE` 문장으로 떨어지지 않게 둔다.
 *
 * ⚠️ **PRD 7.13 D-6 미결**: 이 표의 최종 문구는 `design-manager` 소관이고 아직 확정되지
 * 않았다. 목업이 실제로 쓴 문구는 `PROVIDER_REJECTED` 의 "경계를 못 찾았어요" 하나뿐이라
 * 그것만 그대로 가져오고, 나머지는 D-6 이 요구한 두 가지 성질
 * (① 사람 말, ② **재시도로 풀리는 실패와 다시 찍어야 하는 실패의 구분**)을 지켜 채웠다.
 * D-6 이 닫히면 이 파일만 고친다.
 *
 * ⛔ **금지어가 한 문장에 남아 있다 (2026-09-14 실측, 2026-10-02 갱신. 고치지 않고 올린다).**
 * `NOT_CONFIGURED` 의 "잠시 뒤 **다시 시도**해 주세요" 다. `PROVIDER_FAILED` 에 있던 다른
 * 하나는 PRD `7.6.13b` 인계표(2026-09-27 `design-manager` 확정)로 걷혔다. **PRD 7.5.9 의
 * "다시 거는 일 셋" 표(D-62 판정, 2026-09-13)가 `다시 시도` 를 장에서 통째로 걷었다.**
 * 장에서 다시 거는 일은 금액이 드는가 안 드는가로 갈리고, 그 낱말은 **조회·결제 재요청
 * 쪽에 배정됐다.** D-6 에 남은 것은 PRD 대로 `NOT_CONFIGURED` · `SOURCE_MISSING` ·
 * `SOURCE_TOO_LARGE` · `FALLBACK` 이다.
 *
 * **낱말만 바꾸면 끝나지 않아서 여기서 고치지 않는다.** 이 자리가 가리키는 동작은
 * **공급자를 다시 부르는 일**이라 돈이 든다. 지금 문장은 그것을 공짜처럼 들리게 하고,
 * `다시 만들기` 로 갈면 **금액을 말하지 않고 돈 드는 행동을 권하는** 문장이 된다.
 * **문구는 `design-manager` 정본이고 D-6 과 한 덩어리다**(`docs/DATA_DICTIONARY.md` 16.4).
 */

import type { DetailJobView, JobFailureCode, RecallBlockReason } from "./domain";
import { recallBlockOf } from "./job-rules";
import { MAX_IMAGES_PER_JOB, type UploadBlockReason } from "./upload";

export interface FailureMessage {
  /** 목록 보조줄에 들어가는 짧은 말. 한 줄을 넘기지 않는다. */
  short: string;
  /** 빈/실패 화면 본문. 원인 + 다음에 할 일을 함께 준다. */
  detail: string;
  /**
   * **같은 사진으로 다시 걸어서 풀리는 실패인가.**
   * `false` 면 화면은 다시 거는 대신 "다시 찍기 / 앨범 열기"로 유도해야 한다.
   * 원인이 사진 쪽이면 다시 걸어도 결과가 같은데 **호출은 새로 나간다.** 우리 원가는
   * 그때마다 또 나가고, **다시 만들기라면 셀러 청구도 새로 나간다**(`A-25`). 되살리기
   * 쪽은 2026-09-23 에 닫혔다(`13.1` O-10, PRD `7.5.9`). 더 내실 금액이 없고 그 장에 걸린
   * 금액을 그대로 쓴다. 다시 만들지 않으면 그 금액이 돌아간다.
   *
   * ⛔ **이 값으로 "지금 눌러도 되는가"를 판단하지 마라. 축이 다르다**
   * (`docs/DATA_DICTIONARY.md` 28.5 ③. 2026-09-21).
   * 이 칸이 답하는 것은 **사진 축** 하나다. `NOT_CONFIGURED` 가 `true` 인 것이 그 증거이고
   * 그 판정은 옳다. 사진은 멀쩡하다. **그런데 지금 부르면 또 죽는다** — 막힌 것은 우리
   * 설정·크레딧이다. 이 값으로 `A-25b`(₩500 재결제)를 켜면 **셀러가 실패할 것이 확정된
   * 호출에 돈을 낸다.** `7.13` **D-77** 아래에서 그 호출은 또 한 번의 실패이자 또 한 번의
   * 환불 대상이다.
   *
   * **우리 축은 서버가 판정해 내려야 한다.** 그 칸의 모양은 28.5 ③ 에 있고
   * (`JobImageView.recallBlockedReason`), **값 목록은 D-77 의 남은 절반이 닫혀야 선다.**
   * ⛔ 그때도 이 칸을 그 판정에 흡수시키지 않는다. 두 축을 한 칸에 겹쳐 둔 것이 이 사고의
   * 원인이었다.
   */
  retryable: boolean;
}

const MESSAGES: Record<string, FailureMessage> = {
  PROVIDER_REJECTED: {
    short: "경계를 못 찾았어요",
    detail:
      "상품과 바닥의 색이 비슷하면 경계를 못 찾습니다.\n밝은 단색 위에 올려두고 다시 찍어보세요.",
    retryable: false,
  },
  /** `PROCESSING_INTERRUPTED` 와 갈랐다. 예전에는 두 코드가 한 객체를 함께 가리켰다(데이터
   *  축은 `domain.ts` 의 두 정의, `docs/DATA_DICTIONARY.md` 19.4). 화면이 "우리 쪽에서
   *  끊겼다"고 말하는 순간 그 문장은 이 갈래에서 거짓이 된다. 공급자가 오류를 준 장은
   *  `A-31b` 의 원인 불명 자리로 간다.
   *  글자 정본은 PRD `7.6.13b` 인계표(2026-09-27 `design-manager` 확정)다. 여기서 고르지 않는다.
   *  ⛔ 빈 자리 본문은 `short` 한 줄 그대로다. 코드가 없을 때와 같은 한 줄이어야 셀러가 다른
   *  일로 읽지 않는다. `detail` 은 `A-23`(전량 실패)과 `A-24` 오류 줄에서만 쓴다. */
  PROVIDER_FAILED: {
    short: "왜 안 됐는지 알 수 없어요",
    detail: "왜 안 됐는지 알 수 없습니다.\n사진은 그대로 있습니다.",
    retryable: true,
  },
  /** ⚠️ **기본값(`FALLBACK`)에 맡기면 안 되는 코드다.** 그쪽은 `다시 찍어서 올려 주세요` +
   *  `retryable: false` 인데, 이 장은 **사진이 멀쩡하고 우리가 끊긴 것**이라 둘 다 거짓이다.
   *  셀러에게 다시 찍으라고 시키고 되살리기 버튼까지 감춘다.
   *  `PROVIDER_FAILED` 와 갈랐다. 이 장은 응답을 받은 적이 없고 끊긴 것은 우리 쪽이라, 그
   *  사실을 그대로 말한다. 글자 정본은 PRD `7.6.13b` 인계표(2026-09-27 `design-manager` 확정).
   *  ⭐ 빈 자리 본문이 `detail` 에서 오는 유일한 코드다(목업 `A-31e` 두 줄). 이 `detail` 은
   *  셀러에게 무엇을 시키지 않고 사실만 적어서 그 자리에 올 수 있다.
   *  ⛔ `short` 에 두 문장을 넣지 않는다. `retry-sheet.tsx` 가 `${short}. …` 으로 잇고
   *  `job-labels.ts` 가 행 보조줄로 쓴다. */
  PROCESSING_INTERRUPTED: {
    short: "우리 쪽에서 끊겼어요",
    detail: "만들다가 우리 쪽에서 끊겼습니다.\n사진은 그대로 있습니다.",
    retryable: true,
  },
  NOT_CONFIGURED: {
    short: "지금은 배경을 지울 수 없어요",
    detail:
      "서비스 쪽 준비가 끝나지 않았습니다.\n올린 사진은 그대로 있으니 잠시 뒤 다시 시도해 주세요.",
    retryable: true,
  },
  SOURCE_MISSING: {
    short: "올린 사진을 찾지 못했어요",
    detail: "보관된 원본을 찾지 못했습니다.\n이 장은 다시 올려 주세요.",
    // 같은 사진으로 다시 걸어도 원본이 없으니 결과가 같다. 다시 올리는 것이 유일한 길이다.
    retryable: false,
  },
  SOURCE_TOO_LARGE: {
    short: "사진이 너무 커요",
    detail: "이 장은 처리할 수 있는 크기를 넘었습니다.\n더 작게 찍거나 줄여서 다시 올려 주세요.",
    retryable: false,
  },
  PARTIAL_FAILURE: {
    short: "일부 장을 만들지 못했어요",
    // `실패한 장`은 용어 표의 금지어다(PRD 7.5.9, 2026-09-08 추가). 결과가 안 나온 사진은
    // `못 만든 n장` 이라 부른다. 문장 전체 정리는 아래 D-6 에 그대로 남아 있고 여기서는
    // 낱말만 맞췄다.
    detail: "만들어진 장은 그대로 쓸 수 있습니다.\n못 만든 장만 다시 만들거나 다시 찍어 주세요.",
    // 장마다 원인이 달라 작업 레벨에서 버튼을 켜면 다시 찍어야 할 장에도 돈을 또 쓴다.
    // 재시도 여부는 장 단위로만 묻는다 — `canRetryImage`.
    retryable: false,
  },
  TOTAL_FAILURE: {
    short: "한 장도 만들지 못했어요",
    // 돈 이야기는 이 문자열이 하지 않는다. ⓐ `되돌립니다`는 `7.9` 판 복원이 쓰는 말이라
    // 돈에 쓰면 한 낱말이 두 기능을 가리킨다(PRD 7.5.9), ⓑ 돈 낱말이 나오는 자리에는
    // `실행 방식 미정` 배지가 붙어야 하는데(PRD 7.13 D-21) 이 표는 평문 문자열이라 배지를
    // 달 수 없다. 정본 목업 `A-23` 도 본문은 원인·다음 할 일만 적고 금액은 아래 배너가
    // 말한다 — 그 배너는 화면 쪽에 이미 있다.
    detail:
      "올려주신 사진에서 상품 경계를 찾지 못했습니다.\n밝은 단색 위에 올려두고 다시 찍어 주세요.",
    // 전량 실패는 촬영 조건 자체가 문제라 같은 사진으로 다시 걸면 결과가 같다(A-23).
    // 다시 걸 수 있게 두면 셀러가 같은 돈을 또 쓴다.
    retryable: false,
  },
};

/** 서버가 새 코드를 내려도 화면이 빈 말을 하지 않게 기본값을 둔다.
 * ⚠️ `retryable` 기본값은 `false` — 모르는 실패에 다시 걸게 하면 재과금이다. */
const FALLBACK: FailureMessage = {
  short: "만들지 못했어요",
  detail: "알 수 없는 이유로 실패했습니다.\n다시 찍어서 올려 주세요.",
  retryable: false,
};

export function failureMessage(code: JobFailureCode | null | undefined): FailureMessage {
  if (!code) return FALLBACK;
  return MESSAGES[code] ?? FALLBACK;
}

/** 업로드 단계의 거절은 서버 실패 코드와 다른 축이다(올라가기 전에 걸러진 것).
 *
 * ⛔ **`JOB_FULL` 문장에 수를 글자로 박지 않는다.** 허용 확장자를 `ALLOWED_CONTENT_TYPES`
 * 에서 비추는 것과 같은 규율이고, PRD `7.5.9` 가 "수를 문구에 박지 않는다"로 못 박았다.
 * 상한이 움직이는 날 문장이 함께 움직여야 한다 — 안 그러면 **서버는 새 수로 막는데 화면은
 * 옛 수를 말한다.**
 *
 * ⛔ **`JOB_FULL` 에 `올리지 못했어요` 를 쓰지 않는다.** 용어 표가 그 둘을 갈라 뒀다
 * (PRD `7.5.9` "갈라서 쓰는 것"): **`담지 못했어요` 는 자리가 없어 못 들어간 일**이라
 * 다시 눌러도 같고, **`올리지 못했어요` 는 보내다 실패한 일**이라 다시 누르면 된다.
 * 바꿔 쓰면 셀러가 될 일을 포기하거나 안 될 일을 되풀이한다. */
export function uploadRejectMessage(reason: UploadBlockReason): string {
  switch (reason) {
    case "TOO_LARGE":
      return "파일이 너무 커요";
    case "JOB_FULL":
      return `${MAX_IMAGES_PER_JOB}장이 다 차서 담지 못했어요`;
    case "UPLOAD_CLOSED":
      // ⛔ **`담지 못했어요` 꼬리를 쓰지 않는다.** 훑는 셀러는 꼬리로 성격을 읽는다.
      //    `JOB_FULL` 은 사진을 빼면 되고 이쪽은 빼도 소용없다. 같은 꼬리를 달면
      //    셀러가 사진을 빼려 하고, 빼도 안 되니 앱이 고장 난 것으로 읽는다.
      return "담기가 끝났어요";
    case "UNSUPPORTED_TYPE":
      return "이 형식은 올릴 수 없어요";
    default:
      // 타입이 없는 호출부(JS)가 모르는 사유를 넣었을 때의 자리. 옛 판(삼항식)의 기본값을
      // 그대로 둔다 — 여기서 갈래를 늘리는 것은 이 함수가 아니라 `UploadBlockReason` 이다.
      return "이 형식은 올릴 수 없어요";
  }
}

/** **한 장도 못 담은 회차에 화면이 한 번 하는 말.** 앱·웹이 같은 한 문장을 쓴다.
 *
 * 행마다 서는 사유(`uploadRejectMessage("JOB_FULL")`)와 **자리가 다르다.** 거절된 장에서
 * 일어난 일은 일부만 받았을 때나 한 장도 못 받았을 때나 같아서 **장 사유는 가르지 않는다.**
 * **갈리는 것은 장이 아니라 화면이다** — 한 장도 못 받으면 셀러가 한 동작을 했는데 화면에
 * 진전이 하나도 없고, 그 침묵을 이 줄이 받는다(PRD `7.5.9` 문구 3번 · 판정 3).
 *
 * ⭐ **한 장이라도 받았으면 이 줄을 세우지 않는다.** 받은 장이 스스로 진전을 말한다.
 * 그 판정(**받은 수가 0인가**)은 화면이 한다 — 이 함수는 문장만 준다.
 *
 * ⛔ **`새 작업으로 올려 주세요` 를 붙이지 마라.** 그 화면에서 새 작업을 여는 문이 **실제로
 * 있는지 확인되지 않았다.** 없는 문을 문장으로 그리는 것이 PRD `7.1` 원칙 4가 막는 일이다.
 * **문이 서면 그때 이 문장에 붙인다.** */
export function jobFullNoticeMessage(): string {
  return `이 작업은 ${MAX_IMAGES_PER_JOB}장이 다 찼어요. 고르신 사진은 담지 못했습니다.`;
}

/** **결제로 담기가 끝난 작업에 담으려 했을 때 화면이 한 번 하는 말.**
 *
 * ⛔ **`jobFullNoticeMessage` 와 갈라 쓴다.** 저쪽은 **사진을 빼면 되고** 이쪽은
 * **빼도 소용없다**(`7.5.9` 판정 13). 셀러가 할 일이 반대라 한 문장으로 뭉개면
 * **사진을 빼려다 앱이 고장 난 것으로 읽는다.**
 *
 * ⛔ **갈 곳까지 말한다.** 막기만 하고 길을 안 적으면 셀러가 멈춘다. 다만 **기기별
 * 버튼 이름을 박지 않는다** — 앱과 웹이 달라 한쪽에서 거짓이 된다. */
export function uploadClosedNoticeMessage(): string {
  return "이 작업은 담기가 끝났습니다. 더 만드실 사진은 새 작업에 담아 주세요.";
}

/** **「여기선 아직 못 합니다」에 늘 서 있는 한 문장** (`A-20` 촬영 · `A-21` 앨범).
 *
 * ## 실패 문구 파일에 **실패가 아닌 문장**이 있는 이유
 *
 * 위 둘과 **같은 수를 말하기 때문이다.** 셋이 흩어지면 상한이 움직이는 날 한둘만 따라오고,
 * 그때 화면은 "최대 15장"이라 적어 두고 16장째를 받거나 그 반대를 한다. **한 파일에 두면
 * 다음 사람이 셋을 함께 본다.** 이 문장만 성격이 달라서(거절이 아니라 **막히기 전에 하는
 * 안내**. PRD `7.1` 원칙 4) 이름에 `Message` 를 쓰지 않았다.
 *
 * ⛔ **상태에 따라 이 문장을 바꾸지 마라.** 15장에 닿았다고 글자가 늘거나 줄면 높이가 바뀌어
 * **셔터가 움직인다**(PRD `7.6.4`). 늘 같은 문장으로 서 있고, **지금 몇 장인지는 카운트 필이
 * 말한다.**
 *
 * ⚠️ **앨범 화면은 이 문장이 다른 문장들과 한 문단으로 이어진다.** 그래서 이 함수는 **문장
 * 하나만** 주고 잇는 것은 화면이 한다. ⛔ 여기서 문단을 조립하지 마라 — 조립하면 화면마다
 * 다른 앞뒤가 이 파일로 밀려 들어온다. */
export function imageLimitSentence(): string {
  return `한 작업에는 최대 ${MAX_IMAGES_PER_JOB}장까지 담을 수 있습니다.`;
}

/** ⭐ **`A-31f` 이제 다시 만들 수 없는 장** (PRD `7.6.13g` · `7.5.9` 판정 7 셋째 줄).
 *  `RecallBlockReason` 의 `RETENTION_EXPIRED` 갈래가 쓰는 문장이다. 앱 결과 확인 · 앱 시트 ·
 *  웹 패널 · 웹 결과 확인이 이 한 벌을 읽는다.
 *  ⛔ `SOURCE_GONE`(보관된 원본이 없어…)과 섞지 않는다. 그쪽은 다시 찍어 올리면 되는 장이고
 *  이쪽은 사유는 그대로인데 시간이 지난 장이다.
 *  ⛔ 목업 `l2` 의 뒤 문장(`이 장 금액은 못 만든 날부터 30일에 돌아갑니다.`)을 여기 두지 않는다.
 *  환불 실행 경로가 서기 전에는 셀러에게 보이지 않는다는 법무 선행조건(`LEGAL_IP_GUIDE.md`
 *  `6A.12`)이 있다. 실행 코드가 서는 회차에 붙인다.
 *  ⛔ 쓰지 않는 말: `만료` · `보관 종료` · `삭제됐습니다` · `기한`. 30일은 기산점을 달고 선다. */
export const RETENTION_ENDED_COPY = {
  /** 빈 자리 본문 둘째 줄. 첫 줄은 그 장의 원래 사유다(`retentionEndedCardBody`). */
  cardLine: "원본은 이제 없습니다.",
  /** `!` 배너 첫 줄. */
  head: "이제 다시 만들어 드릴 수 없습니다.",
  /** `!` 배너 둘째 줄. */
  reason: "올린 날부터 30일이 지나 원본이 없습니다.",
  /** 시트·패널의 꺼진 `다시 만들기` 밑에 늘 보이는 사유. 목업에 글자가 없어 위 사실에서 따왔다. */
  disabledReason: "원본이 이제 없어 누를 수 없습니다.",
  /** 웹 격자 칸에 눈에 보이는 짧은 표시. 스크린리더 줄이 같은 사실을 말하므로 화면은 aria-hidden 으로 둔다. */
  cellMark: "원본 없음",
} as const;

/** `A-31f` 빈 자리 본문. 둘째 줄이 `원본은 이제 없습니다.` 다.
 *  **첫 줄은 보관이 끝나기 전 그 빈 자리가 보이던 첫 줄 그대로다**(사유는 그대로이고 시간만
 *  지났다). `PROCESSING_INTERRUPTED` 는 빈 자리가 `detail` 두 줄이라 그 첫 문장
 *  (`만들다가 우리 쪽에서 끊겼습니다.`)을 쓰고, 둘째 문장 `사진은 그대로 있습니다.` 는 이
 *  갈래에서 거짓이라 뺀다. PRD `7.6.13g` 「정하지 못한 것」 1번이 가장 가까운 꼴로 적은 바로 그
 *  꼴이고 2026-10-06 `design-manager` 가 정했다. 다른 코드는 `short` 를 쓰고, 코드가 없으면 원인
 *  불명 줄(`PROVIDER_FAILED.short`)을 쓴다.
 *  `short` 에는 마침표를 붙인다(목업 `A-31f` 는 두 줄 모두 마침표. 2026-10-06 `design-manager`
 *  확정). `short` 자체는 바꾸지 않고 여기서만 붙이며, 이미 마침표로 끝나면 겹쳐 붙이지 않는다. */
export function retentionEndedCardBody(code: JobFailureCode | null | undefined): string {
  let first: string;
  if (code === "PROCESSING_INTERRUPTED") {
    const msg = failureMessage(code);
    first = msg.detail.split("\n")[0] ?? msg.short;
  } else {
    first = failureMessage(code || "PROVIDER_FAILED").short;
  }
  const line = first.endsWith(".") ? first : `${first}.`;
  return `${line}\n${RETENTION_ENDED_COPY.cardLine}`;
}

/** 앱 시트(`A-25c`)·웹 패널이 막힌 사유로 무엇을 적을지 고르는 자리 중 **`A-31f` 갈래만** 답한다.
 *  `RETENTION_EXPIRED` 면 두 줄과 꺼진 버튼 사유를, 아니면 `null` 을 준다. 화면은 `null` 일 때
 *  기존 갈래(`SOURCE_GONE` · `SOURCE_TOO_LARGE` · 막혀 있다)로 내려간다.
 *  ⛔ `SOURCE_GONE` 에서 이 문장을 내지 않는다. 그쪽은 다시 찍어 올리면 되는 장이다.
 *  앱은 두 줄을 줄바꿈으로, 웹은 띄어쓰기로 잇는다. 글자는 같다. */
export function retentionEndedBlockCopy(
  reason: RecallBlockReason | null | undefined,
): { lines: readonly [string, string]; disabledReason: string } | null {
  if (reason !== "RETENTION_EXPIRED") return null;
  return {
    lines: [RETENTION_ENDED_COPY.head, RETENTION_ENDED_COPY.reason],
    disabledReason: RETENTION_ENDED_COPY.disabledReason,
  };
}

/** 웹 `W-31` 결과 확인의 `A-31f` 배너. 보관이 끝난 못 만든 장이 없으면 `null`.
 *  보관은 장마다라 한 작업에 끝난 장과 아직인 장이 섞일 수 있다. 그래서 l1 이 장을 가리킨다
 *  (2026-10-06 design-manager 확정): 한 장이면 `n번 사진은`, 못 만든 장 전부면 `못 만든 k장은`,
 *  일부면 `못 만든 f장 중 k장은`. ⛔ `3번 · 5번 사진` 으로 잇지 않는다(둘 이상이면 어느 장인지는
 *  격자 칸의 `cellMark` 가 가리킨다). 이름은 올린 순번(`sortOrder + 1`)이다.
 *  판정은 앱 결과 확인·시트와 같은 `recallBlockOf(img, view, view.servedAt)` 하나다.
 *  `view` 를 `DetailJobView` 통째로 받는 것은 `recallBlockOf` 의 작업 인자가 그 타입이라서다
 *  (작업 축 판정 `isJobDiscarded` 가 그 안의 칸을 읽는다). 캐스트로 좁히지 않는다. */
export function retentionEndedNotice(
  view: DetailJobView,
): { l1: string; l2: string; imageIds: ReadonlySet<string> } | null {
  const failed = view.images.filter((img) => img.status === "FAILED");
  const ended = failed.filter(
    (img) => recallBlockOf(img, view, view.servedAt) === "RETENTION_EXPIRED",
  );
  const only = ended[0];
  if (!only) return null;
  const subject =
    ended.length === 1
      ? `${only.sortOrder + 1}번 사진은`
      : ended.length === failed.length
        ? `못 만든 ${ended.length}장은`
        : `못 만든 ${failed.length}장 중 ${ended.length}장은`;
  return {
    l1: `${subject} ${RETENTION_ENDED_COPY.head}`,
    l2: RETENTION_ENDED_COPY.reason,
    imageIds: new Set(ended.map((img) => img.id)),
  };
}
